import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseHttpService } from '../base-http.service';

/**
 * Interface for the request to get a signed upload URL
 */
export interface UploadUrlRequest {
  examinationId: string;
  fileName: string;
  roomNumber?: string;
}

/**
 * Interface for the response containing the signed URL
 */
export interface UploadUrlResponse {
  signedUrl: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metaData: Record<string, any>;
}

/**
 * Service for handling file uploads to Google File Storage (GFS) using signed URLs.
 *
 * This service implements a two-stage upload process:
 * 1. Request a signed URL from the backend
 * 2. Upload the file directly to GFS using the signed URL
 */
@Injectable({
  providedIn: 'root',
})
export class GfsUploadService extends BaseHttpService {
  constructor(private readonly http: HttpClient) {
    super();
  }

  /**
   * Requests a signed URL from the backend for file upload
   * @param request - The upload URL request containing examinationId, fileName, and optional roomNumber
   * @returns Observable of the upload URL response
   */
  private requestUploadUrl(
    request: UploadUrlRequest,
  ): Observable<UploadUrlResponse> {
    return this.applyHttpOperators(
      this.http.post<UploadUrlResponse>(
        `${environment.backend_host}/request-upload-url`,
        request,
      ),
    );
  }

  /**
   * Uploads a file using a two-stage process:
   * 1. First requests a signed URL from the backend
   * 2. Then uploads the file directly to GFS using the signed URL
   *
   * @param file - The file to upload
   * @param examinationId - The examination ID
   * @param roomNumber - Optional room number if the image belongs to a room
   * @returns Observable that completes when upload is finished
   */
  uploadFile(
    file: File,
    examinationId: string,
    roomNumber?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    const request: UploadUrlRequest = {
      examinationId,
      fileName: file.name,
      roomNumber,
    };

    return this.requestUploadUrl(request).pipe(
      switchMap((response) => {
        // Upload the file to the signed URL
        // Do NOT set Content-Type header - browser will set it automatically based on file type
        return this.http.put(response.signedUrl, file);
      }),
    );
  }
}
